/**
 * Conversion between the stored email text (sanitised HTML + {{merge_field}} tokens, see the backend
 * event_email_service) and what the contentEditable shows (merge fields as visual chips).
 * Pure DOM functions: unit-tested in jsdom without the browser editing commands.
 */

export interface MergeFieldInfo {
  key: string;
  label: string;
}

const TOKEN_RE = /\{\{\s*([a-z_]+)\s*\}\}/g;
const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Only fields the backend accepts become chips; anything else stays plain text (server rejects on save). */
export function chipHtml(key: string, label: string): string {
  return `<span data-merge-field="${esc(key)}" contenteditable="false" style="background:#E2E8F0;border-radius:4px;padding:0 6px;margin:0 1px;font-size:0.9em;white-space:nowrap;user-select:all;">${esc(label)}</span>`;
}

export function toEditorHtml(stored: string, fields: MergeFieldInfo[]): string {
  const labels = new Map(fields.map((f) => [f.key, f.label]));
  return (stored || '').replace(TOKEN_RE, (whole, key) =>
    labels.has(key) ? chipHtml(key, labels.get(key) as string) : whole
  );
}

const SAFE_HREF = /^(https:\/\/|mailto:)/i;

function serialiseNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return esc(node.textContent || '');
  if (node.nodeType !== Node.ELEMENT_NODE) return '';
  const el = node as HTMLElement;
  const field = el.getAttribute('data-merge-field');
  if (field) return `{{${field}}}`;
  const inner = Array.from(el.childNodes).map(serialiseNode).join('');
  switch (el.tagName.toLowerCase()) {
    case 'b':
    case 'strong':
      return inner ? `<b>${inner}</b>` : '';
    case 'i':
    case 'em':
      return inner ? `<i>${inner}</i>` : '';
    case 'br':
      return '<br>';
    case 'a': {
      const href = el.getAttribute('href') || '';
      return SAFE_HREF.test(href) ? `<a href="${esc(href)}">${inner}</a>` : inner;
    }
    case 'div':
    case 'p':
      // contentEditable wraps lines in <div>; the stored form uses <p>.
      return inner.replace(/<br>$/, '') ? `<p>${inner.replace(/<br>$/, '')}</p>` : '';
    default:
      return inner; // unknown tags (span, font, ...) are dropped, their text kept
  }
}

export function fromEditorHtml(editorHtml: string): string {
  const root = document.createElement('div');
  root.innerHTML = editorHtml || '';
  if (!(root.textContent || '').trim() && !root.querySelector('[data-merge-field]')) return '';
  const hasBlocks = Array.from(root.children).some((c) => ['DIV', 'P'].includes(c.tagName));
  const out = Array.from(root.childNodes).map(serialiseNode).join('');
  if (!out.trim()) return '';
  if (hasBlocks) {
    // Loose text next to blocks (browser quirk on the first line): wrap each run in its own <p>.
    const parts: string[] = [];
    let loose = '';
    Array.from(root.childNodes).forEach((n) => {
      const isBlock = n.nodeType === Node.ELEMENT_NODE && ['DIV', 'P'].includes((n as HTMLElement).tagName);
      if (isBlock) {
        if (loose.trim()) parts.push(`<p>${loose}</p>`);
        loose = '';
        parts.push(serialiseNode(n));
      } else {
        loose += serialiseNode(n);
      }
    });
    if (loose.trim()) parts.push(`<p>${loose}</p>`);
    return parts.join('');
  }
  return `<p>${out}</p>`;
}

/** Plain-text fields (subject, title, button label): insert a token at the caret. */
export function insertToken(value: string, key: string, start: number, end: number): { value: string; caret: number } {
  const token = `{{${key}}}`;
  return { value: value.slice(0, start) + token + value.slice(end), caret: start + token.length };
}

export function normaliseContent<T extends { subject: string; title: string; blocks: string[]; buttons: any[]; name: string; content?: unknown }>(c: T) {
  return JSON.stringify({
    name: c.name,
    subject: c.subject,
    title: c.content ? '' : c.title,
    blocks: c.content ? [] : c.blocks,
    content: c.content || null,
    buttons: (c.content ? [] : c.buttons).map((b) => ({ label: b.label, colour: normaliseButtonColour(b.colour), target: b.target, url: b.target === 'external' ? b.url || '' : '' })),
  });
}

/** Legacy colours that map onto a palette colour (old "Bleu nuit FFM" is sent as Bleu FFM). */
export const normaliseButtonColour = (c?: string | null): string => {
  const v = (c || '').toLowerCase();
  return v === '#232751' ? '#253473' : v;
};

/** The FFM palette: the colours the email layout itself uses. */
export const BUTTON_COLOURS: { value: string; label: string }[] = [
  { value: '#ff0e0e', label: 'Rouge FFM' },
  { value: '#253473', label: 'Bleu FFM' },
];

export const BUTTON_TARGETS: { value: 'rsvp_form' | 'event_page' | 'external'; label: string }[] = [
  { value: 'rsvp_form', label: 'Formulaire RSVP' },
  { value: 'event_page', label: "Page de l'événement" },
  { value: 'external', label: 'Lien externe' },
];

